import React, { useState, useRef, useEffect } from 'react';
import { ColorBlindnessService, ColorBlindnessType } from '../../lib/services/colorBlindnessService';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType } from '../../lib/types/EncodingOperation';
import './ColorBlindnessSimulator.css';

export const ColorBlindnessSimulator: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string>('');
  const [simulatedImageUrl, setSimulatedImageUrl] = useState<string>('');
  const [selectedCVDType, setSelectedCVDType] = useState<ColorBlindnessType>('deuteranopia');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadFeedback, setDownloadFeedback] = useState<'idle' | 'downloading' | 'error'>('idle');
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const processTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Process image with debouncing
  useEffect(() => {
    if (selectedFile) {
      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
      }

      processTimeoutRef.current = setTimeout(() => {
        processImage();
      }, 300);
    }

    return () => {
      if (processTimeoutRef.current) {
        clearTimeout(processTimeoutRef.current);
      }
    };
  }, [selectedFile, selectedCVDType]);

  const processImage = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);

    try {
      const result = await ColorBlindnessService.simulate(selectedFile, selectedCVDType);

      if (result.status === 'error') {
        setError(result.errorMessage || 'Processing failed');
        setSimulatedImageUrl('');
      } else if (result.outputData) {
        const url = URL.createObjectURL(result.outputData as Blob);
        setSimulatedImageUrl(url);
      }
    } catch (err) {
      const errorMessage = ErrorHandler.handle(err, EncoderToolType.COLOR_BLINDNESS_SIMULATOR);
      setError(errorMessage);
      setSimulatedImageUrl('');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (file: File) => {
    // Cleanup previous URLs
    if (originalImageUrl) {
      URL.revokeObjectURL(originalImageUrl);
    }
    if (simulatedImageUrl) {
      URL.revokeObjectURL(simulatedImageUrl);
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setOriginalImageUrl(url);
    setError(null);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDownload = () => {
    if (!simulatedImageUrl) return;

    setDownloadFeedback('downloading');

    try {
      const link = document.createElement('a');
      link.href = simulatedImageUrl;
      link.download = `${selectedFile?.name.split('.')[0]}_${selectedCVDType}.${selectedFile?.type.split('/')[1]}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        setDownloadFeedback('idle');
      }, 2000);
    } catch (err) {
      setDownloadFeedback('error');
      setTimeout(() => {
        setDownloadFeedback('idle');
      }, 2000);
    }
  };

  const handleClearImage = () => {
    if (originalImageUrl) {
      URL.revokeObjectURL(originalImageUrl);
    }
    if (simulatedImageUrl) {
      URL.revokeObjectURL(simulatedImageUrl);
    }
    setSelectedFile(null);
    setOriginalImageUrl('');
    setSimulatedImageUrl('');
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const cvdTypes: ColorBlindnessType[] = [
    'protanopia',
    'protanomaly',
    'deuteranopia',
    'deuteranomaly',
    'tritanopia',
    'tritanomaly'
  ];

  const selectedCVDInfo = ColorBlindnessService.getCVDInfo(selectedCVDType);

  // Cleanup URLs on unmount
  useEffect(() => {
    return () => {
      if (originalImageUrl) {
        URL.revokeObjectURL(originalImageUrl);
      }
      if (simulatedImageUrl) {
        URL.revokeObjectURL(simulatedImageUrl);
      }
    };
  }, []);

  return (
    <div className="color-blindness-simulator">
      <div className="container">
        <header className="header">
          <h1>Color Blindness Simulator</h1>
          <p className="description">
            Test your designs for accessibility by simulating different types of color vision deficiency (CVD)
          </p>
        </header>

        {/* File Upload */}
        <div className="upload-section">
          <div
            className={`drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            role="button"
            tabIndex={0}
            aria-label="Upload image file"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
          >
            <svg className="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className="drop-zone-text">
              <strong>Click to upload</strong> or drag and drop
            </p>
            <p className="drop-zone-hint">PNG, JPEG, WEBP, GIF (max 10MB)</p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleFileInputChange}
            className="file-input"
            aria-label="File input"
          />
        </div>

        {selectedFile && (
          <div className="file-info">
            <div className="file-details">
              <strong>Selected file:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </div>
            <button
              onClick={handleClearImage}
              className="btn-clear"
              aria-label="Clear selected image"
            >
              Clear
            </button>
          </div>
        )}

        {/* CVD Type Selector */}
        <div className="cvd-selector">
          <label htmlFor="cvd-type" className="label">
            Color Vision Deficiency Type:
          </label>
          <select
            id="cvd-type"
            value={selectedCVDType}
            onChange={(e) => setSelectedCVDType(e.target.value as ColorBlindnessType)}
            className="select"
            disabled={!selectedFile || isProcessing}
          >
            {cvdTypes.map((type) => {
              const info = ColorBlindnessService.getCVDInfo(type);
              return (
                <option key={type} value={type}>
                  {info.name} - {info.description}
                </option>
              );
            })}
          </select>
          <p className="cvd-info">
            <strong>Prevalence:</strong> {selectedCVDInfo.prevalence}
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-message" role="alert">
            <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}

        {/* Image Comparison */}
        {originalImageUrl && (
          <div className="comparison-section">
            <div className="image-container">
              <div className="image-wrapper">
                <h3 className="image-title">Original</h3>
                <img
                  src={originalImageUrl}
                  alt="Original uploaded image"
                  className="preview-image"
                />
              </div>
              <div className="image-wrapper">
                <h3 className="image-title">
                  {selectedCVDInfo.name}
                  {isProcessing && <span className="processing-badge">Processing...</span>}
                </h3>
                {simulatedImageUrl ? (
                  <img
                    src={simulatedImageUrl}
                    alt={`Image as seen with ${selectedCVDInfo.name}`}
                    className="preview-image"
                  />
                ) : (
                  <div className="placeholder">
                    {isProcessing ? 'Processing...' : 'No simulation available'}
                  </div>
                )}
              </div>
            </div>

            {/* Download Button */}
            {simulatedImageUrl && (
              <button
                onClick={handleDownload}
                className="btn-download"
                disabled={downloadFeedback === 'downloading'}
                aria-label="Download simulated image"
              >
                {downloadFeedback === 'downloading' ? 'Downloading...' : 'Download Simulated Image'}
              </button>
            )}
          </div>
        )}

        {/* Progressive Enhancement Fallback */}
        <noscript>
          <div className="fallback-form">
            <h3>JavaScript Required</h3>
            <p>This tool requires JavaScript to simulate color blindness in real-time.</p>
            <p>For command-line processing, consider using tools like:</p>
            <pre><code>
              # Using ImageMagick for color space conversion
              convert input.jpg -colorspace sRGB output.jpg
            </code></pre>
          </div>
        </noscript>
      </div>
    </div>
  );
};
